import { useMemo, useState } from 'react';
import { missingKasChains } from '../../components/kasPromoCode';
import { App, Button, Card, Popover, Space, Tag, Tooltip, Typography } from 'antd';
import { DataTable } from '../../components/DataTable';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import { StatusTag } from '../../components/StatusTag';
import type { ColumnsType } from 'antd/es/table';
import { Gift, Info, PencilSimple, Plus } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys } from '../catalog/api';
import { ConfigEditModal } from '../config/ConfigEditModal';
import { configApi, configKeys, type SystemConfig } from '../config/api';
import { tokens } from '../../theme';
import {
  KIND_LABELS,
  KIND_TAG_COLOR,
  VOUCHER_CONFIG_KEYS,
  VOUCHER_MAX_PER_BILL_KEY,
  rewardApi,
  rewardGroupOf,
  rewardKeys,
  rewardStatusLabel,
  summarizeConditions,
  summarizeRewardValue,
  type Reward,
  type UpsertRewardPayload,
} from './api';
import { RewardFormModal } from './RewardFormModal';


function formatConfigValue(config: SystemConfig): string {
  return config.unit ? `${config.value} ${config.unit}` : String(config.value);
}

export function RewardsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<Reward | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SystemConfig | null>(null);
  const deductHint = useDismissibleHint('rewards-deduct-points');

  const rewardsQuery = useQuery({ queryKey: rewardKeys.all, queryFn: rewardApi.list });
  const configsQuery = useQuery({ queryKey: configKeys.all, queryFn: configApi.list });

  // Tỷ giá định giá quà — chỉ để gợi ý và cảnh báo trên form khai quà, không dùng để tính giá lúc
  // đổi. `null` khi chưa tải được: form ẩn hẳn phần gợi ý thay vì đoán một con số.
  const vndPerPoint =
    (configsQuery.data ?? []).find((c) => c.key === 'Rewards:VndPerPoint')?.value ?? null;
  const chainsQuery = useQuery({ queryKey: catalogKeys.chains, queryFn: catalogApi.listChains });
  const branchesQuery = useQuery({ queryKey: catalogKeys.branches, queryFn: catalogApi.listBranches });

  const voucherConfigs = useMemo(
    () =>
      (configsQuery.data ?? []).filter((c) =>
        (VOUCHER_CONFIG_KEYS as readonly string[]).includes(c.key),
      ),
    [configsQuery.data],
  );

  const systemMaxPerBill = useMemo(
    () => (configsQuery.data ?? []).find((c) => c.key === VOUCHER_MAX_PER_BILL_KEY)?.value,
    [configsQuery.data],
  );

  const chainNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const chain of chainsQuery.data ?? []) map.set(chain.id, chain.name);
    return map;
  }, [chainsQuery.data]);

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: UpsertRewardPayload }) =>
      payload.id ? rewardApi.update(payload.id, payload.body) : rewardApi.create(payload.body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rewardKeys.all });
      message.success('Đã lưu quà.');
      setEditing(null);
      setIsCreating(false);
    },
    onError: (error) => reportError(error, 'Không lưu được quà.'),
  });

  const configMutation = useMutation({
    mutationFn: ({ key, value, reason }: { key: string; value: number; reason: string }) =>
      configApi.set(key, value, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: configKeys.all });
      setEditingConfig(null);
      message.success('Đã lưu quy tắc chung.');
    },
    onError: (error) => reportError(error, 'Không lưu được quy tắc.'),
  });

  const columns: ColumnsType<Reward> = [
    {
      title: 'Quà',
      key: 'name',
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          {row.description && (
            <div style={{ fontSize: 12, color: tokens.color.ink3 }}>{row.description}</div>
          )}
        </>
      ),
    },
    {
      title: 'Loại · nhóm',
      dataIndex: 'kind',
      width: 130,
      // Màu chip là NHÓM, không phải loại: nhóm tiền tô cam nhấn, nhóm hiện vật để trung tính (ADR-009).
      render: (k: Reward['kind']) => <Tag color={KIND_TAG_COLOR[k]}>{KIND_LABELS[k]}</Tag>,
    },
    {
      title: 'Giá điểm',
      dataIndex: 'pointsCost',
      width: 100,
      render: (p: number) => p.toLocaleString('vi-VN'),
    },
    {
      title: 'Giá trị',
      key: 'value',
      width: 160,
      render: (_, row) => summarizeRewardValue(row),
    },
    {
      title: 'Tồn kho',
      dataIndex: 'stockRemaining',
      width: 100,
      render: (s: number | null, row) =>
        rewardGroupOf(row.kind) === 'InKind'
          ? '—'
          : s === null
            ? 'Không giới hạn'
            : s.toLocaleString('vi-VN'),
    },
    {
      title: 'Hạn dùng',
      dataIndex: 'validDays',
      width: 90,
      render: (d: number) => `${d} ngày`,
    },
    {
      title: 'Điều kiện',
      key: 'conditions',
      render: (_, row) => (
        <span style={{ fontSize: 12.5 }}>{summarizeConditions(row.conditions)}</span>
      ),
    },
    {
      title: 'Chuỗi',
      key: 'chain',
      width: 120,
      render: (_, row) =>
        row.chainId ? (chainNameById.get(row.chainId) ?? '—') : 'Toàn hệ thống',
    },
    {
      // Mã là tuỳ chọn, nên thiếu mã KHÔNG phải lỗi — dùng tông cảnh báo, không phải lỗi. Cột này
      // tồn tại để bắt chỗ "khai xong rồi quên" trước khi ra quầy, chứ không để chặn ai.
      title: 'Mã KAS',
      key: 'kas',
      width: 150,
      render: (_, row) => {
        const missing = missingKasChains(row.kasPromoCodes, row.chainId, chainsQuery.data ?? []);
        if (missing.length === 0) return <StatusTag color="success">Đã khai</StatusTag>;
        return (
          <Tooltip title={`Chưa khai cho: ${missing.join(', ')}`}>
            <StatusTag color="warning">Thiếu {missing.length} chuỗi</StatusTag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 110,
      render: (_, row) => {
        const s = rewardStatusLabel(row);
        return <StatusTag color={s.tone}>{s.label}</StatusTag>;
      },
    },
    {
      title: 'Đã phát',
      dataIndex: 'issuedCount',
      width: 80,
      render: (n: number) => n.toLocaleString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 80,
      render: (_, row) => (
        <Button
          size="small"
          icon={<PencilSimple size={14} />}
          aria-label={`Sửa ${row.name}`}
          onClick={() => setEditing(row)}
        >
          Sửa
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Quà & voucher"
        subtitle="Danh mục quà đổi điểm · điều kiện voucher · quy tắc chung hệ thống"
        hint={<HintToggle hint={deductHint} />}
        actions={
          <Space>
            <Popover
              placement="bottomRight"
              title="Cách đọc nhóm quà & cộng dồn ưu đãi"
              content={
                <div style={{ maxWidth: 380 }}>
                  <Typography.Paragraph style={{ fontSize: 12.5, marginBottom: 10 }}>
                    <b>Màu chip là NHÓM, không phải loại.</b> Nhóm tiền (Voucher tiền · Giảm %) tô cam;
                    nhóm hiện vật (Món ăn · Mua 1 tặng 1 · Combo) để trung tính. Hạn mức mỗi hóa đơn
                    đếm <b>tổng</b> voucher — không chia nhóm, không chia loại.
                  </Typography.Paragraph>
                  <Typography.Paragraph style={{ fontSize: 12.5, marginBottom: 0 }}>
                    <b>Mặc định một hóa đơn một ưu đãi</b>, cộng chiết khấu hạng thẻ. Muốn hai ưu đãi nằm
                    chung thì phải bật <b>“Cho phép dùng chung”</b> ở <b>cả hai</b> món quà — bật một bên
                    là không đủ — <b>và</b> nới “Tối đa trên một hóa đơn” lên từ 2. Chiết khấu hạng thẻ
                    không phải ưu đãi nên luôn đi kèm.
                  </Typography.Paragraph>
                </div>
              }
            >
              <Button icon={<Info size={16} aria-hidden />}>Cách tính</Button>
            </Popover>
            <Button type="primary" icon={<Plus size={16} />} onClick={() => setIsCreating(true)}>
              Thêm quà
            </Button>
          </Space>
        }
      />
      <PageBody>
        <HintBanner
          hint={deductHint}
          message="Chưa có trừ điểm thẳng vào hóa đơn"
          description="Khách đổi điểm lấy voucher trước, rồi nhân viên áp voucher đó tại quầy."
        />

        {(rewardsQuery.isPending || configsQuery.isPending) && <LoadingState />}

        {rewardsQuery.isError && (
          <ErrorState error={rewardsQuery.error} onRetry={() => void rewardsQuery.refetch()} />
        )}

        {rewardsQuery.isSuccess && (
          <>
            <Card
              size="small"
              title={
                <Space>
                  <Gift size={16} />
                  Quy tắc chung cho voucher
                </Space>
              }
              style={{ marginBottom: tokens.space.sectionGap }}
              loading={configsQuery.isPending}
            >
              {configsQuery.isError ? (
                <Typography.Text type="danger">Không tải được quy tắc chung.</Typography.Text>
              ) : (
                <DataTable toolbar={false}
                  rowKey="key"
                  size="small"
                  pagination={false}
                  dataSource={voucherConfigs}
                  columns={[
                    {
                      title: 'Thông số',
                      key: 'key',
                      render: (_, config) => (
                        <>
                          <div style={{ fontWeight: 600 }}>
                            {config.key.split(':').slice(1).join(' · ')}
                          </div>
                          <div style={{ fontSize: 12, color: tokens.color.ink2 }}>
                            {config.description}
                          </div>
                        </>
                      ),
                    },
                    {
                      title: 'Đang dùng',
                      key: 'value',
                      width: 160,
                      render: (_, config) => formatConfigValue(config),
                    },
                    {
                      title: '',
                      key: 'actions',
                      width: 80,
                      render: (_, config) => (
                        <Button size="small" onClick={() => setEditingConfig(config)}>
                          Sửa
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </Card>

            <Card styles={{ body: { paddingTop: 8 } }}>
              <DataTable
                title="Danh mục đổi quà"
                subTitle={`${rewardsQuery.data.length} món`}
                onReload={() => void rewardsQuery.refetch()}
                searchPlaceholder="Tìm quà"
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={rewardsQuery.data}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                scroll={{ x: 1200 }}
              />
            </Card>
          </>
        )}
      </PageBody>

      <RewardFormModal
        reward={editing}
        isCreating={isCreating}
        chains={chainsQuery.data ?? []}
        branches={branchesQuery.data ?? []}
        vndPerPoint={vndPerPoint}
        systemMaxPerBill={systemMaxPerBill}
        submitting={saveMutation.isPending}
        onClose={() => {
          setEditing(null);
          setIsCreating(false);
        }}
        onSubmit={(body) => {
          if (isCreating) {
            saveMutation.mutate({ body });
          } else if (editing) {
            saveMutation.mutate({ id: editing.id, body });
          }
        }}
      />

      <ConfigEditModal
        config={editingConfig}
        submitting={configMutation.isPending}
        onCancel={() => setEditingConfig(null)}
        onSubmit={(value, reason) => {
          if (editingConfig) {
            configMutation.mutate({ key: editingConfig.key, value, reason });
          }
        }}
      />
    </>
  );
}
